import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment, useCallback, useEffect } from 'react'
import FilterSection from 'src/components/FilterSection/FilterSection'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NavAwayBox, { NavAwayProps } from 'src/components/NavAwayBox/NavAwayBox'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import PendingRequestsTable from 'src/components/PendingRequestsTable/PendingRequestsTable'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useWindowScrollBottomHandler } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { css } from 'styled-components'

export interface PendingRequestsBoxInfiniteScrollProps {
  fetch: (filtersBody: VirtualCardsFiltersBody, page?: number) => void
  page?: number
  loading: boolean
  complete: boolean
}

interface PendingRequestsBoxProps {
  className?: string
  virtualCards: VirtualCardListModel[]
  isSnapshot?: boolean
  navAway?: NavAwayProps
  infiniteScrollProps: PendingRequestsBoxInfiniteScrollProps
  cardApproverScrollProps?: PendingRequestsBoxInfiniteScrollProps
  isUserCardApprover?: boolean
  cardApprovalRulesFetched: boolean
  showLoadingPage: boolean
  totalCount: number
}

const PendingRequestsBox: React.FC<PendingRequestsBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const handleScroll = useCallback((): void => {
    if (
      !props.infiniteScrollProps.loading &&
      !props.infiniteScrollProps.complete &&
      props.cardApprovalRulesFetched
    ) {
      if (
        props.isUserCardApprover &&
        props.cardApproverScrollProps !== undefined
      ) {
        props.cardApproverScrollProps?.fetch(
          {},
          props.infiniteScrollProps.page !== undefined
            ? props.infiniteScrollProps.page + 1
            : 0,
        )
      } else {
        props.infiniteScrollProps.fetch(
          {},
          props.infiniteScrollProps.page !== undefined
            ? props.infiniteScrollProps.page + 1
            : 0,
        )
      }
    }
  }, [
    props.infiniteScrollProps.loading,
    props.infiniteScrollProps.complete,
    props.infiniteScrollProps.fetch,
    props.infiniteScrollProps.page,
    props.cardApproverScrollProps?.loading,
    props.cardApproverScrollProps?.complete,
    props.cardApproverScrollProps?.fetch,
    props.cardApprovalRulesFetched,
  ])
  useWindowScrollBottomHandler(handleScroll)

  useEffect((): void => {
    if (
      props.cardApprovalRulesFetched &&
      props.isUserCardApprover &&
      props.cardApproverScrollProps !== undefined
    ) {
      props.cardApproverScrollProps?.fetch({}, 0)
    }
  }, [props.cardApprovalRulesFetched, props.isUserCardApprover])

  return props.showLoadingPage ? (
    <LoadingBackgroundPage />
  ) : (
    <NavAwayBox
      className={props.className}
      navAway={props.isSnapshot ? props.navAway : undefined}
      loading={props.infiniteScrollProps.loading}
    >
      <FilterSection
        topLeftContent={
          <Fragment>
            {props.isSnapshot && (
              <ObjectBoxTitle
                title={translate('Pending Requests')}
                proportional={{
                  viewable: props.virtualCards.length,
                  total: props.totalCount,
                }}
              />
            )}
            {!props.isSnapshot && (
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate('${count} Pending Requests', {
                  count: `${props.totalCount}`,
                })}
              </span>
            )}
          </Fragment>
        }
      />
      <div
        css={css`
          background-color: ${styleConstants.colors.gray_lightest};
          ${!props.isSnapshot &&
          css`
            border-bottom-left-radius: ${styleConstants.borderRadius.box};
            border-bottom-right-radius: ${styleConstants.borderRadius.box};
            overflow-y: visible;
          `}
        `}
      >
        {props.virtualCards.length > 0 && (
          <PendingRequestsTable virtualCards={props.virtualCards} />
        )}
        {props.virtualCards.length === 0 &&
          props.infiniteScrollProps.complete && (
            <TableEmptyState design={TableEmptyStateDesigns.PendingRequests} />
          )}
      </div>
    </NavAwayBox>
  )
}

export default PendingRequestsBox
